import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import ChatWidget from "@/components/site/ChatWidget";
import CookieBanner from "@/components/site/CookieBanner";
import { captureAttribution, trackEvent } from "@/lib/tracking";

import Home from "@/pages/Home";
import Services from "@/pages/Services";
import About from "@/pages/About";
import CaseStudies from "@/pages/CaseStudies";
import CaseStudyDetail from "@/pages/CaseStudyDetail";
import Resources from "@/pages/Resources";
import ResourceDetail from "@/pages/ResourceDetail";
import Contact from "@/pages/Contact";
import NotFound from "@/pages/NotFound";
import Work from "@/pages/Work";

import Marketo from "@/pages/services/Marketo";
import Salesforce from "@/pages/services/Salesforce";
import CampaignOps from "@/pages/services/CampaignOps";
import ManagedServices from "@/pages/services/ManagedServices";
import MartechIntegration from "@/pages/services/MartechIntegration";
import Reporting from "@/pages/services/Reporting";

import Privacy from "@/pages/legal/Privacy";
import Cookies from "@/pages/legal/Cookies";
import Terms from "@/pages/legal/Terms";

function RouteTracker() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // Wait a tick for the target page to render, then scroll to the anchor
      const id = hash.replace("#", "");
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        // Element may not have mounted yet — retry once after layout.
        setTimeout(() => {
          document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 250);
      }
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
    trackEvent("page_view", { path: pathname });
  }, [pathname, hash]);
  return null;
}

export default function App() {
  useEffect(() => {
    captureAttribution();
    trackEvent("page_view", { path: window.location.pathname });
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <RouteTracker />
        <Navbar />
        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/marketo-services" element={<Marketo />} />
            {/* Legacy URL — keep old links/SERP results working, 301-style redirect to the canonical route */}
            <Route path="/services/marketo-engage" element={<Navigate to="/marketo-services" replace />} />
            <Route path="/services/salesforce-crm" element={<Salesforce />} />
            <Route path="/services/campaign-operations" element={<CampaignOps />} />
            <Route path="/services/managed-services" element={<ManagedServices />} />
            <Route path="/services/martech-integration" element={<MartechIntegration />} />
            <Route path="/services/reporting-attribution" element={<Reporting />} />
            <Route path="/case-studies" element={<CaseStudies />} />
            <Route path="/case-studies/:slug" element={<CaseStudyDetail />} />
            <Route path="/work" element={<Work />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/resources/:slug" element={<ResourceDetail />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy-policy" element={<Privacy />} />
            <Route path="/cookie-policy" element={<Cookies />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        <Footer />
        <ChatWidget />
        <CookieBanner />
        <Toaster position="bottom-center" />
      </BrowserRouter>
    </div>
  );
}
